/**
 * Tuning presets.
 *
 * A tuning is nothing but six MIDI notes, low string first. The standard set
 * below is generated by shifting one reference shape, but the type is the
 * general one on purpose: custom and drop presets are additional entries in
 * {@link TUNINGS}, not a different concept.
 */

import { noteName, type Notation } from './notation'

/** One string of a tuning preset. */
export interface GuitarString {
  /** Stable id, unique within a tuning. */
  readonly id: string
  /** Target MIDI note when the string is open. */
  readonly midi: number
}

/** A named set of open string targets, listed low to high. */
export interface Tuning {
  readonly id: string
  /** Short label, e.g. "E Standard". */
  readonly name: string
  readonly strings: readonly GuitarString[]
}

/** Standard six-string shape at concert pitch: E2 A2 D3 G3 B3 E4. */
export const STANDARD_SHAPE: readonly number[] = [40, 45, 50, 55, 59, 64]

/** Build the string list for an arbitrary set of MIDI notes. */
export function stringsFromMidi(midis: readonly number[]): readonly GuitarString[] {
  return midis.map((midi, index) => ({ id: `${String(index)}-${String(midi)}`, midi }))
}

/** Build a tuning by shifting {@link STANDARD_SHAPE} by whole semitones. */
function shiftedStandard(id: string, name: string, semitones: number): Tuning {
  return {
    id,
    name,
    strings: stringsFromMidi(STANDARD_SHAPE.map((midi) => midi + semitones)),
  }
}

/** Which group a preset belongs to in the picker. */
export type TuningGroup = 'standard' | 'alternate'

/**
 * The six standard tunings, each a semitone below the previous.
 *
 * Named after the note the lowest string lands on, which is how guitarists
 * refer to them.
 */
export const STANDARD_TUNINGS: readonly Tuning[] = [
  shiftedStandard('e-standard', 'E Standard', 0),
  shiftedStandard('eb-standard', 'Eb Standard', -1),
  shiftedStandard('d-standard', 'D Standard', -2),
  shiftedStandard('db-standard', 'Db Standard', -3),
  shiftedStandard('c-standard', 'C Standard', -4),
  shiftedStandard('b-standard', 'B Standard', -5),
]

/**
 * Altered tunings, which are not a transposition of the standard shape.
 *
 * These are the reason a tuning is stored as six MIDI notes rather than a
 * root plus an offset: DADGAD puts two strings a whole tone apart, which no
 * shift of the standard shape ever produces.
 */
export const ALTERNATE_TUNINGS: readonly Tuning[] = [
  { id: 'drop-d', name: 'Drop D', strings: stringsFromMidi([38, 45, 50, 55, 59, 64]) },
  { id: 'open-g', name: 'Open G', strings: stringsFromMidi([38, 43, 50, 55, 59, 62]) },
  { id: 'dadgad', name: 'DADGAD', strings: stringsFromMidi([38, 45, 50, 55, 57, 62]) },
]

/** Every preset, standards first. */
export const TUNINGS: readonly Tuning[] = [...STANDARD_TUNINGS, ...ALTERNATE_TUNINGS]

/** Presets grouped for the picker. */
export const TUNING_GROUPS: readonly {
  group: TuningGroup
  label: string
  tunings: readonly Tuning[]
}[] = [
  { group: 'standard', label: 'Standard', tunings: STANDARD_TUNINGS },
  { group: 'alternate', label: 'Alternativ', tunings: ALTERNATE_TUNINGS },
]

/** Upper bound for a capture band, in cents. */
export const MAX_CAPTURE_RANGE_CENTS = 180

/**
 * How far from its target a measurement may sit and still belong to a string.
 *
 * Half the closest interval in the tuning, capped: two strings a whole tone
 * apart, as in DADGAD, leave only 100 cents each before their bands would
 * meet. A fixed range wide enough for standard tuning would hand notes to the
 * wrong string there.
 *
 * @param strings - Open strings, ascending.
 * @returns Half-width of each string's band, in cents.
 */
export function captureRangeFor(
  strings: readonly GuitarString[],
  max = MAX_CAPTURE_RANGE_CENTS,
): number {
  let closest = Number.POSITIVE_INFINITY
  for (let i = 1; i < strings.length; i += 1) {
    const gap = Math.abs((strings[i]?.midi ?? 0) - (strings[i - 1]?.midi ?? 0))
    if (gap > 0) closest = Math.min(closest, gap)
  }
  if (!Number.isFinite(closest)) return max
  return Math.min(max, (closest * 100) / 2)
}

/** The default tuning: concert-pitch standard. */
export const DEFAULT_TUNING: Tuning = TUNINGS[0]!

/** Look up a tuning by id, falling back to E Standard. */
export function tuningById(id: string | null): Tuning {
  return TUNINGS.find((tuning) => tuning.id === id) ?? DEFAULT_TUNING
}

/**
 * Spell out a tuning's strings, e.g. "E A D G B E".
 *
 * Follows the chosen notation and transposition so the menu agrees with the
 * meters.
 */
export function tuningSpelling(tuning: Tuning, notation: Notation, transposition = 0): string {
  return tuning.strings.map((string) => noteName(string.midi, notation, transposition)).join(' ')
}
